import { NewAssociationType, NewMemberOfType, NewOrderPartyType } from 'types/order';

export const getInitials = (name?: string) => {
  const cleanName = (name || '').trim();

  if (!cleanName) return '?';

  const [first, second] = cleanName.split(/\s+/);

  return (second ? `${first.charAt(0)}${second.charAt(0)}` : cleanName.substring(0, 2)).toUpperCase();
};

export const uniqueByName = <T extends { name: string }>(items?: T[] | null): T[] =>
  (items || []).filter(
    (item, index, list) => list.findIndex((other) => other.name === item.name) === index
  );

export const uniqueKinds = (kinds?: string[] | null): string[] =>
  Array.from(new Set((kinds || []).filter(Boolean)));

const ROLE_ACRONYMS = new Set([
  'AI',
  'ALTA',
  'CD',
  'DBA',
  'EIN',
  'FHA',
  'HOA',
  'INC',
  'IRA',
  'IRS',
  'LLC',
  'LLP',
  'LP',
  'PA',
  'PC',
  'PITI',
  'PLLC',
  'POA',
  'RON',
  'SSN',
  'TRID',
  'USA',
  'VA',
]);

const SMALL_WORDS = new Set(['and', 'at', 'by', 'for', 'in', 'of', 'on', 'the', 'to']);

export const formatRoleName = (value?: string) => {
  const clean = (value || '').trim();

  if (!clean) return '';

  return clean
    .split(/\s+/)
    .map((word, index) => {
      const upper = word.toUpperCase();
      const lower = word.toLowerCase();

      if (ROLE_ACRONYMS.has(upper)) return upper;
      if (index > 0 && SMALL_WORDS.has(lower) && word === upper) return lower;
      if (word === upper || word === lower) {
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      }

      return word;
    })
    .join(' ');
};

type PartyFilterItem = {
  id?: string;
  name: string;
  kinds?: string[];
  member_of?: { name: string }[];
};

const normalizeSearch = (value?: string) => (value || '').toLocaleLowerCase();

export const filterPartiesByQuery = <T extends PartyFilterItem>(
  parties: T[],
  query: string,
  selectedId?: string
): T[] => {
  const search = normalizeSearch(query).trim();

  if (!search) return parties;

  return parties.filter(
    (party) =>
      (!!selectedId && party.id === selectedId) ||
      normalizeSearch(party.name).includes(search) ||
      (party.kinds || []).some((kind) => normalizeSearch(kind).includes(search)) ||
      (party.member_of || []).some((member) => normalizeSearch(member.name).includes(search))
  );
};

export const uniqueParties = (parties?: NewOrderPartyType[] | null): NewOrderPartyType[] => {
  const seen = new Set<string>();

  return (parties || []).filter((party) => {
    if (!party?._id || seen.has(party._id)) return false;
    seen.add(party._id);
    return true;
  });
};

// The API can return the same link twice (for example from both sides of the
// association). Identity is the linked party plus the role it is linked as.
export const associationKey = (association: NewAssociationType) =>
  `${association.party_id}|${association.party_kind}|${association.linked_as}`;

export const uniqueAssociations = (
  associations?: NewAssociationType[] | null
): NewAssociationType[] => {
  const seen = new Set<string>();

  return (associations || []).filter((association) => {
    const key = associationKey(association);

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

export const sortAssociations = (associations: NewAssociationType[]) =>
  [...associations].sort(
    (associationA, associationB) =>
      (associationA.party_name || '').localeCompare(associationB.party_name || '') ||
      (associationA.linked_as || '').localeCompare(associationB.linked_as || '') ||
      (associationA.party_kind || '').localeCompare(associationB.party_kind || '')
  );

export const normalizeAssociations = (
  associations?: NewAssociationType[] | null
): NewAssociationType[] => sortAssociations(uniqueAssociations(associations));

// A member can legitimately be related with more than one kind, so the kind is
// part of the identity while exact duplicates collapse.
export const relationKey = (member: NewMemberOfType) => `${member._id}|${member.kind}`;

export const uniqueRelations = (members?: NewMemberOfType[] | null): NewMemberOfType[] => {
  const seen = new Set<string>();

  return (members || []).filter((member) => {
    const key = relationKey(member);

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

export const sortRelations = (members: NewMemberOfType[]) =>
  [...members].sort(
    (memberA, memberB) =>
      (memberA.name || '').localeCompare(memberB.name || '') ||
      (memberA.kind || '').localeCompare(memberB.kind || '')
  );

export const normalizeRelations = (members?: NewMemberOfType[] | null): NewMemberOfType[] =>
  sortRelations(uniqueRelations(members));
